/**
 * Admin page caches + login-time prefetch (stale-while-revalidate).
 *
 * Every data-backed Admin tab renders instantly from these module-level
 * caches; a background refresh still runs on each visit and updates rows in
 * place. DashboardLayout warms everything right after an ADMIN logs in
 * (prefetchAdminData), so even the first click into Admin shows content.
 *
 * Covered tabs:
 *  - User Management  → users list + per-user program grants
 *  - Measure Logic    → react-query ['measures'] (prefetched via queryClient)
 *  - Chart Abstraction → questions document
 *  - System Settings  → payment-adjustment configs (all years)
 * (Practices reads localStorage and Year Locking reads ProgramYearContext —
 *  both already instant.)
 *
 * Non-admins never trigger the prefetch. AuthContext.logout() clears the
 * caches so nothing leaks across accounts on a shared browser.
 */
import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const authHeaders = (token: string) => ({ Authorization: `Bearer ${token}` });

// ===== User Management =====

export const adminUsersCache = {
  users: [] as any[],
  programs: {} as Record<number, string[]>,
};

/** Fetch users + per-user program grants and write them into the cache. */
export async function fetchAdminUsers(token: string): Promise<any[]> {
  const response = await fetch(`${apiUrl}/api/users`, { headers: authHeaders(token) });
  if (!response.ok) throw new Error('Failed to fetch users');
  const data = await response.json();
  adminUsersCache.users = data;
  // P4: load each user's program grants (small user counts; parallel).
  try {
    const entries = await Promise.all(
      data
        .filter((u: any) => u.id !== 0)
        .map(async (u: any) => {
          const res = await apiClient.get(`/api/programs/users/${u.id}`);
          return [u.id, res?.programs ?? []] as const;
        })
    );
    adminUsersCache.programs = Object.fromEntries(entries);
  } catch {
    // Grants column degrades to badges-only; not fatal.
  }
  return data;
}

// ===== Measure Logic (react-query owns this cache) =====

/** Shared fetcher for the ['measures'] query (MeasureLibraryTable + prefetch). */
export async function fetchMeasuresList(): Promise<any[]> {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${apiUrl}/api/measures/list`, {
    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to fetch measures');
  return response.json();
}

// ===== Chart Abstraction =====

export const chartAbstractionCache = {
  loaded: false,
  document: null as any | null,
};

export async function fetchChartAbstractionDocument(token: string): Promise<any | null> {
  const response = await fetch(`${apiUrl}/api/chart-abstraction-questions`, {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error('Failed to fetch document');
  const data = await response.json();
  chartAbstractionCache.document = data.exists ? data.document : null;
  chartAbstractionCache.loaded = true;
  return chartAbstractionCache.document;
}

// ===== System Settings: payment-adjustment configs (all years) =====

export const paymentAdjustmentCache = {
  loaded: false,
  configs: [] as any[],
};

export async function fetchPaymentAdjustmentConfigs(token: string): Promise<any[]> {
  const response = await fetch(`${apiUrl}/api/admin/payment-adjustment-config`, {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new Error('Failed to fetch configurations');
  const data = await response.json();
  paymentAdjustmentCache.configs = data;
  paymentAdjustmentCache.loaded = true;
  return data;
}

// ===== Prefetch orchestration =====

let prefetchStarted = false;

/** Fire-and-forget warmup of every Admin tab (admins only — caller checks the role). */
export function prefetchAdminData(token: string, queryClient: QueryClient): void {
  if (prefetchStarted) return;
  prefetchStarted = true;
  const retryLater = () => { prefetchStarted = false; };
  if (adminUsersCache.users.length === 0) fetchAdminUsers(token).catch(retryLater);
  queryClient
    .prefetchQuery({
      queryKey: ['measures'],
      queryFn: fetchMeasuresList,
      // Default gcTime (5 min) would evict the unobserved prefetch before the
      // admin ever opens the tab; keep it around for the session.
      gcTime: 12 * 60 * 60 * 1000,
    })
    .catch(() => { /* react-query refetches on mount if this failed */ });
  if (!chartAbstractionCache.loaded) fetchChartAbstractionDocument(token).catch(() => {});
  if (!paymentAdjustmentCache.loaded) fetchPaymentAdjustmentConfigs(token).catch(() => {});
}

export function clearAdminCaches(): void {
  adminUsersCache.users = [];
  adminUsersCache.programs = {};
  chartAbstractionCache.loaded = false;
  chartAbstractionCache.document = null;
  paymentAdjustmentCache.loaded = false;
  paymentAdjustmentCache.configs = [];
  prefetchStarted = false;
}
